<template>
  <ModalDefault
    :id="MODAL_ID"
    size="md"
    :button="false"
    :show-footer="true"
  >
    <template #modal-title>
      <div class="flex items-center gap-2">
        <MdiIcon icono="mdiCardAccountDetailsOutline" class="w-4 h-4" />
        <span class="text-base font-bold text-gray-800">
          Corregir identificación {{ nombrePerfil }}
        </span>
      </div>
    </template>

    <template #modal-body>
      <div class="flex flex-col gap-4">
        <AlertDefault
          titulo="Operación sensible"
          :texto="`La identificación ${nombrePerfil} se usa en los registros asociados. Verifique el dato antes de guardar; esta corrección queda registrada en auditoría.`"
          color="yellow"
          icono="ExclamationTriangleIcon"
        />

        <div class="rounded-lg border border-gray-300 dark:border-gray-700 p-3">
          <h3
            class="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2"
          >
            Datos actuales
          </h3>
          <div class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p class="text-gray-500 dark:text-gray-400">Tipo de identificación</p>
              <p class="font-medium text-gray-800 dark:text-white">
                {{ nombreTipoActual || '—' }}
              </p>
            </div>
            <div>
              <p class="text-gray-500 dark:text-gray-400">Número de identificación</p>
              <p class="font-mono font-medium text-gray-800 dark:text-white">
                {{ props.identificacionActual || '—' }}
              </p>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div class="relative">
            <SelectDefault
              id="corregir_tipo_identificacion"
              v-model="form.copeTipoIdentificacion"
              label="Nuevo tipo de identificación"
              :required="true"
              :options="props.tiposIdentificacion"
              :map-options="(item: object) => {
                const i = item as IConfMaestra
                return {
                  optionText: i.comaNombrelargo,
                  optionValue: i.comaId as number
                }
              }"
              :error="showError('form.copeTipoIdentificacion')"
            />
          </div>
          <div class="relative">
            <InputDefault
              v-model="form.copeIdentificacion"
              label="Nuevo número de identificación"
              type="text"
              :required="true"
              :maxlength="20"
              :error="showError('form.copeIdentificacion')"
            />
          </div>
        </div>

        <div
          v-if="props.perfil === 'pacientes'"
          class="flex items-start gap-3 rounded-lg border border-gray-300 dark:border-gray-700 p-3"
        >
          <SwitchDefault
            id="corregir_snapshots"
            v-model="form.corregirSnapshots"
            :true-value="true"
            :false-value="false"
            size="small"
          />
          <div>
            <label
              for="corregir_snapshots"
              class="block text-sm font-semibold text-gray-800 dark:text-white cursor-pointer"
            >
              Corregir también el histórico de atenciones
            </label>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Actualiza el documento congelado en las atenciones ya registradas
              ({{ TABLAS_SNAPSHOT.join(', ') }}). Si lo deja desactivado, el histórico
              conservará el documento anterior.
            </p>
          </div>
        </div>
      </div>
    </template>

    <template #modal-footer>
      <div class="flex items-center justify-end gap-2 w-full">
        <ButtonDefault size="sm" @click="cerrar"> Cancelar </ButtonDefault>
        <ButtonDefault
          color="blue"
          size="sm"
          :is-loading="guardando"
          @click="confirmar"
        >
          <MdiIcon icono="mdiContentSaveOutline" class="w-4 h-4" />
          Corregir identificación
        </ButtonDefault>
      </div>
    </template>
  </ModalDefault>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import ModalDefault from '@/modules/Core/components/Layout/ModalDefault.vue'
import MdiIcon from '@/modules/Core/components/general/MdiIcon.vue'
import ButtonDefault from '@/modules/Core/components/general/ButtonDefault.vue'
import AlertDefault from '@/modules/Core/components/general/AlertDefault.vue'
import InputDefault from '@/modules/Core/components/Form/InputDefault.vue'
import SelectDefault from '@/modules/Core/components/Form/SelectDefault.vue'
import SwitchDefault from '@/modules/Core/components/Form/SwitchDefault.vue'
import { openModal, closeModal } from '@/modules/Core/usings/generales'
import { personaService } from '@/services'
import type { IConfMaestra } from '@/modules/Configuracion/interfaces'
import type { ICorreccionIdentificacion } from '@/modules/Administracion/interfaces'
import { useValidator, validators } from '@/modules/Core/composables/useValidator'
import { normalizarIdentificacion } from '@/modules/Administracion/utils/identificacionPersona'

const MODAL_ID = 'modal-corregir-identificacion'
// Solo informativo para el usuario; el backend decide qué tablas existen realmente
const TABLAS_SNAPSHOT = ['atenciones', 'admisiones']

const props = defineProps<{
  copeId: number
  perfil?: 'pacientes' | 'empleados' | 'empresas'
  identificacionActual?: string | null
  tipoIdentificacionActual?: number | null
  tiposIdentificacion: IConfMaestra[]
}>()
const nombrePerfil = computed(() => {
  if (props.perfil === 'empresas') return 'de la empresa'
  return props.perfil === 'empleados' ? 'del empleado' : 'del paciente'
})

const emit = defineEmits<{
  (e: 'actualizado', payload: ICorreccionIdentificacion): void
}>()

const guardando = ref(false)

const form = reactive({
  copeTipoIdentificacion: null as number | null,
  copeIdentificacion: '',
  corregirSnapshots: false
})

const validator = useValidator({
  form: {
    copeTipoIdentificacion: validators.requerido,
    copeIdentificacion: (value: unknown) => {
      const identificacion = normalizarIdentificacion(value)
      if (!identificacion) return 'Este campo es requerido'
      if (identificacion.length < 5) return 'Mínimo 5 caracteres'
      if (!/^[A-Za-z0-9-]+$/.test(identificacion)) {
        return 'Solo se admiten letras, números y guiones'
      }
      return true
    }
  },
  refs: { form: ref(form) }
})
const { validate, showError, reset } = validator

const nombreTipoActual = computed(
  () =>
    props.tiposIdentificacion.find(
      item => item.comaId === props.tipoIdentificacionActual
    )?.comaNombrelargo ?? ''
)

const hayCambios = computed(() => {
  const identificacionCambia =
    normalizarIdentificacion(form.copeIdentificacion) !==
      normalizarIdentificacion(props.identificacionActual)
  const tipoCambia =
    Number(form.copeTipoIdentificacion) !== Number(props.tipoIdentificacionActual)
  return identificacionCambia || tipoCambia
})

const guardar = async () => {
  guardando.value = true
  window.notificacion.loading().circle()
  try {
    const identificacion = normalizarIdentificacion(form.copeIdentificacion)
    const { data } = props.perfil === 'empleados' || props.perfil === 'empresas'
      ? await personaService[props.perfil === 'empresas' ? 'fixIdentificacionEmpresa' : 'fixIdentificacionEmpleado'](props.copeId, {
          copeTipoIdentificacion: form.copeTipoIdentificacion as number,
          copeIdentificacion: identificacion
        })
      : await personaService.fixIdentificacionPaciente(props.copeId, {
          copeTipoIdentificacion: form.copeTipoIdentificacion as number,
          copeIdentificacion: identificacion,
          corregirSnapshots: form.corregirSnapshots
        })
    const result = data.body
    const totalSnapshots = (result?.snapshots ?? []).reduce(
      (acc, item) => acc + (item.registros ?? 0),
      0
    )
    window.notificacion.notificacion(
      'success',
      form.corregirSnapshots
        ? `Identificación corregida. Registros históricos actualizados: ${totalSnapshots}`
        : 'Identificación corregida'
    )
    emit('actualizado', result)
    cerrar()
  } catch {
    // errorHandle de BaseService ya notificó el detalle al usuario
  } finally {
    guardando.value = false
    window.notificacion.loading().remove()
  }
}

const confirmar = () => {
  if (!validate()) {
    window.notificacion.notificacion(
      'failure',
      'Por favor, corrija los errores del formulario'
    )
    return
  }
  if (!hayCambios.value) {
    window.notificacion.notificacion('warning', 'No se detectaron cambios')
    return
  }

  const tipoAnterior = props.tiposIdentificacion.find(
    item => item.comaId === props.tipoIdentificacionActual
  )
  const tipoNuevo = props.tiposIdentificacion.find(
    item => item.comaId === form.copeTipoIdentificacion
  )
  const nombreTipoAnterior =
    tipoAnterior?.comaNombrecorto ??
    tipoAnterior?.comaNombrelargo ??
    String(props.tipoIdentificacionActual ?? '—')
  const nombreTipoNuevo =
    tipoNuevo?.comaNombrecorto ??
    tipoNuevo?.comaNombrelargo ??
    String(form.copeTipoIdentificacion ?? '—')

  window.notificacion.confirmacion(
    'Confirmar corrección',
    `Se cambiará la identificación de ${nombreTipoAnterior} (${props.identificacionActual ?? '—'}) a ${nombreTipoNuevo} (${normalizarIdentificacion(form.copeIdentificacion)})${
      form.corregirSnapshots ? ', incluyendo el histórico de atenciones' : ''
    }. ¿Desea continuar?`,
    () => {
      guardar()
    },
    () => {}
  )
}

const cerrar = () => closeModal(MODAL_ID)

const open = () => {
  form.copeTipoIdentificacion = props.tipoIdentificacionActual ?? null
  form.copeIdentificacion = normalizarIdentificacion(props.identificacionActual)
  form.corregirSnapshots = false
  reset()
  openModal(MODAL_ID, false)
}

defineExpose({ open })
</script>
